import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useEditor } from '../context/EditorContext';
import { editingStore, isEditingText, paintTextFormat } from '../lib/editingStore';

/**
 * Format Painter (Home ▸ Clipboard), Publisher / Word style.
 *
 *  - Text: highlight text (or put the caret in it), click Format Painter — the character formatting at the start of the
 *    highlight and its paragraph's formatting are copied (`captureTextFormat`, HomeTab). Then highlight the text that
 *    should look the same: it takes that formatting (`paintTextFormat`) and the painter switches off. A bare click paints
 *    nothing — the painter waits until text is highlighted. Double-click the button to keep it on; Esc (or clicking the
 *    button again) stops it.
 *  - Object: with an object selected (nothing being edited) the painter copies the object's formatting and applies it
 *    to the next object(s) selected (RibbonBar's effect) — one use, or until Esc after a double-click.
 *
 * `state.formatPainter` = { mode: 'text' | 'object', sticky, sourceId, format? , properties? }. This hook (mounted once,
 * in RibbonBar) watches the text selection while a text painter is armed and shows the paintbrush cursor.
 */
const UI_ONLY = '[data-ribbon], [data-dialog], [role="dialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper], [data-keep-editing]';

const highlightInEditedBox = () => {
  if (!isEditingText()) return false;
  const sel = window.getSelection();
  return !!(sel && sel.rangeCount && !sel.isCollapsed && editingStore.el.contains(sel.anchorNode) && sel.toString().length > 0);
};

export const useFormatPainter = () => {
  const { state, rawDispatch } = useEditor();
  const painter = state.formatPainter;
  const painterRef = useRef(painter);
  painterRef.current = painter;

  // paintbrush cursor over the text while armed (index.css: body.wp-painting)
  useEffect(() => {
    if (!painter) return undefined;
    document.body.classList.add('wp-painting');
    document.body.classList.toggle('wp-painting-sticky', !!painter.sticky);
    return () => { document.body.classList.remove('wp-painting'); document.body.classList.remove('wp-painting-sticky'); };
  }, [painter]);

  useEffect(() => {
    if (!painter || painter.mode !== 'text') return undefined;
    let pressedInText = false; // a mouse press inside the edited text may start a new highlight
    const stop = () => rawDispatch({ type: 'SET_FORMAT_PAINTER', payload: null });
    const paint = () => {
      const fp = painterRef.current;
      if (!fp || fp.mode !== 'text' || !highlightInEditedBox()) return; // a bare click: wait for a highlight
      if (!paintTextFormat(fp.format)) return;
      toast.success(fp.sticky ? 'Formatting painted — highlight more text, or press Esc to stop.' : 'Formatting painted.', { id: 'format-painter' });
      if (!fp.sticky) stop();
    };
    const onMouseDown = (e) => { pressedInText = !!(isEditingText() && editingStore.el.contains(e.target)); };
    const onMouseUp = (e) => {
      if (!pressedInText || e.target.closest?.(UI_ONLY)) return;
      pressedInText = false;
      window.setTimeout(paint, 0); // the browser finalises the selection (also a double-click's word) after mouseup
    };
    // keyboard highlight: Shift+arrows, painted when Shift is released
    const onKeyUp = (e) => { if (e.key === 'Shift') paint(); };
    // Esc while editing text: cancel the painter (the box stays in edit mode); CanvasWorkspace handles Esc otherwise
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || !isEditingText()) return;
      e.stopPropagation();
      e.preventDefault();
      stop();
      toast.info('Format Painter off.', { id: 'format-painter' });
    };
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [painter, rawDispatch]);
};
